import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.shoppingGuide'), to: '/usfans-spreadsheet-guide' },
    { label: t('nav.categories'), to: '/#categories' },
    { label: t('nav.faq'), to: '/#faq' },
  ];

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/' && !location.hash;
    if (to.startsWith('/#')) {
      return location.pathname === '/' && location.hash === to.slice(1);
    }
    return location.pathname === to || location.pathname.startsWith(to + '/');
  };

  return (
    <header className="sticky top-0 z-40 border-b-2 border-background-200 bg-white">
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-white">
            <i className="ri-link-m text-lg"></i>
          </span>
          <span className="text-xl font-extrabold tracking-tight text-foreground-950">
            USFans<span className="text-primary-600">Link</span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-bold transition-colors ${
                isActive(link.to) ? 'text-primary-700' : 'text-foreground-800 hover:text-foreground-950'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            {t('nav.buyNow')}
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
            aria-label="Menu"
          >
            <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-background-200 bg-white px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm font-bold text-foreground-800 hover:bg-background-100"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <p className="mt-5 px-3 text-xs font-bold uppercase tracking-wide text-foreground-500">
            {t('nav.categories')}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {categories.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-2 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
              >
                <span className="h-9 w-9 shrink-0 overflow-hidden rounded-md">
                  <img
                    src={cat.image}
                    alt={t(`category.${cat.key}`)}
                    title={`${t(`category.${cat.key}`)} — USFans Spreadsheet`}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <span className="truncate">{t(`category.${cat.key}`)}</span>
              </a>
            ))}
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="mt-4 block rounded-md bg-primary-500 px-3 py-3 text-center text-sm font-bold text-white"
          >
            {t('nav.buyNow')}
          </a>
        </div>
      )}
    </header>
  );
}